<script setup lang="ts">
import AppointmentScreen, {
  type AppointmentScreenResultData,
} from './AppointmentScreen.vue';
import { SlideTransition } from '@glow/shared/components/transitions';
import SuccessfullyAppointedScreen from './SuccessfullyAppointedScreen.vue';
import { AuthForm } from '@glow/shared/features/auth';
import { graphql } from '@/gql';
import { useQuery } from '@urql/vue';
import { useVModel } from '@vueuse/core';

const props = defineProps<{
  modelValue?: boolean;
  workPlaceId: number;
}>();
defineEmits<{
  (e: 'update:modelValue', modelValue: boolean): void;
}>();

const isVisible = useVModel(props, 'modelValue');
const { data: queryData } = useQuery({
  query: graphql(/* GraphQL */ `
    query MasterAppointmentModal_MasterWorkPlaceQuery($id: Int!) {
      masterWorkPlace(id: $id) {
        id
        workPeriods {
          id
          start: startedAt
          end: endedAt
        }
        cartServices {
          id
          partnerService {
            id
            price
            duration
          }
        }
      }
    }
  `),
  variables: computed(() => ({
    id: props.workPlaceId,
  })),
});
const workPeriods = toRef(
  () => queryData.value?.masterWorkPlace.workPeriods ?? []
);

const activeDate = ref<Date>(new Date());
const summary = computed(() => {
  const services = queryData.value?.masterWorkPlace.cartServices ?? [];
  return services.reduce(
    (summary, value) => {
      summary.price += value.partnerService.price;
      summary.duration += value.partnerService.duration;
      return summary;
    },
    { serviceCount: services.length, price: 0, duration: 0 }
  );
});

const isLoggedIn = useIsLoggedIn();
type MsaterAppointmentModalScreen = 'auth' | 'appointment' | 'done';
const currentScreen = ref<MsaterAppointmentModalScreen>(
  isLoggedIn.value ? 'appointment' : 'auth'
);

const { executeMutation: appoint, fetching: isAppointing } = useMutation(
  graphql(/* GraphQL */ `
    mutation MasterAppointmentModal_AppointToMasterMutation(
      $workPlaceId: Int!
      $input: AppointInput!
    ) {
      appointToMaster(workPlaceId: $workPlaceId, input: $input) {
        id
      }
    }
  `)
);

function onAuthenticate() {
  currentScreen.value = 'appointment';
}
async function onAppoint(data: AppointmentScreenResultData) {
  await appoint({
    workPlaceId: props.workPlaceId,
    input: {
      dateTime: data.dateTime,
      note: data.note,
      withHomeVisit: false,
    },
  });
  currentScreen.value = 'done';
}
function onDone() {
  isVisible.value = false;
}
</script>

<template>
  <AppModal v-model="isVisible" size="sm">
    <div>
      <SlideTransition mode="out-in" direction="left">
        <AuthForm v-if="currentScreen === 'auth'" @done="onAuthenticate" />
        <AppointmentScreen
          v-else-if="currentScreen === 'appointment'"
          v-model:active-date="activeDate"
          :price="summary?.price ?? 0"
          :duration="summary?.duration ?? 0"
          :appointable-periods="workPeriods"
          :is-loading="isAppointing"
          @appoint="onAppoint"
        />
        <SuccessfullyAppointedScreen
          v-else-if="currentScreen === 'done'"
          @done="onDone"
        />
      </SlideTransition>
    </div>
  </AppModal>
</template>
